import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, { Column, Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import SubHeader from "../../../../components/Generic/SubHeader";
import { PRIVATE_ROUTES } from "../../../../constants";
import { Movement as IMovement } from "../../../../models/movement";
import { Sort } from "../../../../models/shipeezi.table";
import { User } from "../../../../models/user";
import { useActions } from "./hooks/useAction";
import { MovementsGrid, PrintContainer } from "./styles";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName, ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

export default function Movement() {
  const { t: translate } = useTranslation();
  const {
    movementsData,
    getMovements,
    pageSize,
    handlePageSize,
    page,
    handleCurrentPage,
    total,
    getMovementsCount,
    handleSort,
    handleSearch,
    setSorting,
  } = useActions();

  const [disableByWhomSorting, setDisableByWhomSorting] = useState<boolean>(false);
  const { currentUser, userPermissions } = useAppState();
  const [warehousesToView, setWarehousesToView] = useState<any[]>([]);
  const [movementPermission, setMovementPermission] = useState<{
    create: boolean;
    print: boolean;
  }>({
    create: false,
    print: false,
  });

  const history = useHistory();

  const { isFetching } = getMovements();

  getMovementsCount();

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setMovementPermission({
        create:
          checkResourceCan(userPermissions, ResourceName.MOVEMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_MOVEMENT_ADD_NEW),
        print:
          checkResourceCan(userPermissions, ResourceName.MOVEMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_MOVEMENT_PRINT),
      });
      const movementResourceId = userPermissions?.find((permission) => {
        return (
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE &&
          permission?.objectDetails?.name === ResourceName.MOVEMENT
        );
      })?.objectDetails?.id;

      const warehouses = userPermissions
        ?.map((permission) => {
          if (
            permission?.objectType === ResourceObjectTypeEnum.FUNCTION &&
            permission?.objectDetails?.objectData === "warehouse" &&
            permission?.resourceId === movementResourceId
          ) {
            return permission;
          }
        })
        ?.filter((permission) => !!permission);

      setWarehousesToView(warehouses);
    }
  }, [userPermissions]);

  const movementLocationsFrom = (movement?: IMovement) => {
    const warehouseFrom = movement?.warehouseFrom?.name;
    const sectionFrom = movement?.sectionFrom?.name;
    const mapCellNameFrom = movement?.mapCellNameFrom;

    return (
      <>
        {warehouseFrom ? <div>{warehouseFrom}</div> : <></>}
        {sectionFrom ? <div>{sectionFrom}</div> : <></>}
        {mapCellNameFrom ? <div>{mapCellNameFrom}</div> : <></>}
      </>
    );
  };

  const movementLocationsTo = (movement?: IMovement) => {
    const warehouseTo = movement?.warehouseTo?.name;
    const sectionTo = movement?.sectionTo?.name;
    const mapCellNameTo = movement?.mapCellNameTo;

    return (
      <>
        {warehouseTo ? <div>{warehouseTo}</div> : <></>}
        {sectionTo ? <div>{sectionTo}</div> : <></>}
        {mapCellNameTo ? <div>{mapCellNameTo}</div> : <></>}
      </>
    );
  };

  const columns: Column[] = [
    {
      name: "objectNumber",
      title: translate("object"),
      hideSort: true,
      formatComponent: (_, data?: IMovement) => {
        if (data?.itemJobId && data?.itemJob) {
          const { number, description } = data.itemJob;

          return `${number || "#"}. ${description || ""}`;
        }
        if (data?.storageBoxId && data?.storageBox) return data?.storageBox?.number || "";

        return "";
      },
    },
    {
      name: "reference",
      title: translate("reference"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (_, data?: IMovement) => {
        if (data?.itemJobId && data?.itemJob) return data.itemJob.job?.reference || "";

        return "";
      },
    },
    {
      name: "customer",
      title: translate("customer"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (_, data?: IMovement) => {
        if (data?.itemJobId && data?.itemJob) {
          return `${data.itemJob.job?.customer?.name} ${data.itemJob.job?.customer?.lastName}`;
        }

        return "";
      },
    },
    {
      name: "storageBoxType",
      searchIdentifier: "storageBoxType",
      title: translate("type"),
      hideSearch: true,
      hideSort: true,
      formatComponent: (_, data?: IMovement) => {
        if (data?.itemJobId && data?.itemJob) return translate("looseItem");
        if (data?.storageBoxId && data?.storageBox && data?.storageBox?.storageBoxType)
          return data?.storageBox?.storageBoxType?.name || "";

        return "";
      },
    },
    {
      name: "mapCellNameFrom",
      title: translate("from"),
      formatComponent: (_, data?: IMovement) => movementLocationsFrom(data),
    },
    {
      name: "mapCellNameTo",
      title: translate("to"),
      formatComponent: (_, data?: IMovement) => movementLocationsTo(data),
    },
    {
      name: "createdAt",
      title: translate("when"),
      formatText: (date: string) =>
        currentUser?.preferences
          ? formatDateWithUserPref(date, currentUser.preferences, "dateOnly")
          : "",
      hideSearch: true,
    },
    {
      name: "user",
      searchIdentifier: "user",
      title: translate("byWho"),
      formatText: (user?: User) => user?.fullName || "",
      hideSort: disableByWhomSorting,
      associationColumnSettings: {
        whenSearching: {
          clearSorting: setSorting,
          disableSorting: setDisableByWhomSorting,
        },
        columnToSorting: "full_name",
      },
    },
  ];

  const commentMovement = () => {
    return (
      <Tooltip title={translate("funcionalityUnderDevelopment")} placement="top">
        <div>
          <GridIcons.email size={18} color="#D0D5DD" onClick={() => {}} />
        </div>
      </Tooltip>
    );
  };

  const printMovement = () => {
    return (
      <PrintContainer title={translate("funcionalityUnderDevelopment")} placement="top">
        <div>
          <GridIcons.print size={26} color="#D0D5DD" onClick={() => {}} />
        </div>
      </PrintContainer>
    );
  };

  return (
    <div data-cy="movementsGrid">
      {/* <SubHeader
        title={translate("movements")}
        showWarehouseSelect
        warehousesToView={warehousesToView}
      /> */}
      <SubHeader showWarehouseSelect warehousesToView={warehousesToView} />
      <MovementsGrid>
        <ShipeeziNewGrid
          data={movementsData}
          columns={columns}
          keyExtractor={(_, index) => String(index)}
          totalItems={total}
          loadingData={isFetching}
          emptyData={translate("noInformationToBeShown")}
          pageSizeList={[15, 40, 60, 100]}
          pageSize={pageSize}
          onChangePageSize={handlePageSize}
          currentPage={page}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          onSorting={(sorts: Sort[]) => handleSort(sorts)}
          showSelection={false}
          positionActionsColumn="right"
          generalActions={{
            orderBy: {
              left: ["handleSearch"],
              right: ["handlePrintMovement", "handleNewRecord"],
            },
            handleSearch: {
              onSearch: (filters) => handleSearch(filters),
            },
            handleNewRecord: isShowAction(currentUser, movementPermission.create)
              ? {
                  text: translate("newMovement"),
                  action: () => history.push(PRIVATE_ROUTES.MOVEMENT_MNT),
                }
              : undefined,
            handlePrintMovement: isShowAction(currentUser, movementPermission.print)
              ? printMovement()
              : undefined,
          }}
          rowActions={{
            orderBy: ["handleComment"],
          }}
          rowActionsExtra={[
            {
              title: translate("comment"),
              key: "handleComment",
              buttonAndAction: commentMovement,
            },
          ]}
        />
      </MovementsGrid>
    </div>
  );
}
